import { AlertTriangle, Check, Loader2, Save, Sparkles } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { SuggestSlideDialog } from '@/components/canvas/SuggestSlideDialog';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { useEditorActions, useScript, useSyncStatus } from '@/store/editor.store';

export function TopBar() {
  const script = useScript();
  const actions = useEditorActions();
  const [suggestOpen, setSuggestOpen] = useState(false);

  const onLabel = useCallback((value: string) => actions.updateMeta({ label: value }), [actions]);

  return (
    <header className="flex h-14 shrink-0 items-center gap-4 border-b border-border bg-bg-panel px-5">
      <Link to="/" className="flex items-center gap-2 text-[15px] font-semibold tracking-tight">
        <span className="text-accent">LMS</span>
        <span className="text-fg">Authoring Studio</span>
      </Link>

      <div className="h-6 w-px bg-border" />

      <input
        value={script?.label ?? ''}
        onChange={(e) => onLabel(e.target.value)}
        placeholder="Untitled module"
        className="min-w-[180px] flex-1 max-w-[420px] bg-transparent text-sm font-medium text-fg placeholder:text-fg-subtle focus:outline-none"
      />

      <div className="ml-auto flex items-center gap-2">
        <Button
          size="sm"
          variant="soft"
          onClick={() => setSuggestOpen(true)}
          disabled={!script}
          title="Suggest the next slide with AI"
        >
          <Sparkles size={14} className="text-accent" />
          Suggest next slide
        </Button>
        <SuggestSlideDialog open={suggestOpen} onClose={() => setSuggestOpen(false)} />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => alert('Preview mode is coming soon')}
        >
          Preview
        </Button>
        <Link
          to="/"
          className="inline-flex h-8 items-center justify-center rounded-md px-3 text-[13px] font-medium text-fg-muted transition-colors hover:bg-bg-subtle hover:text-fg"
        >
          Drafts
        </Link>
        <SaveStatusButton />
        <UserAvatar />
      </div>
    </header>
  );
}

/**
 * Single source of truth for "is my work saved?" on the editor toolbar.
 *
 * Replaces the previous mislabeled "Publish" + duplicate cloud-icon pair.
 * Publishing actually happens from the pack detail page (`/publish` endpoint
 * lives there); this button only flushes the debounced sync queue, which is
 * the "save" operation users wanted.
 *
 * Status mapping (drives label + icon + colour):
 *   - idle    → "Saved" (disabled checkmark — nothing to flush)
 *   - pending → "Save changes" (primary — there are queued edits)
 *   - saving  → "Saving…" (spinner — flush in flight)
 *   - error   → "Retry save" (danger — last flush failed; click to retry)
 *
 * Clicking always forces an immediate flush (bypasses the 600ms debounce).
 */
function SaveStatusButton() {
  const { status, error } = useSyncStatus();
  const script = useScript();
  const { flush } = useEditorActions();
  const [retrying, setRetrying] = useState(false);

  const onClick = async () => {
    setRetrying(true);
    try {
      await flush();
    } finally {
      setRetrying(false);
    }
  };

  if (!script) return null;

  if (status === 'saving' || retrying) {
    return (
      <Button size="sm" variant="ghost" disabled title="Sync in flight…">
        <Loader2 size={14} className="animate-spin text-fg-muted" />
        Saving…
      </Button>
    );
  }
  if (status === 'error') {
    return (
      <Button
        size="sm"
        variant="danger"
        onClick={onClick}
        title={error ? `Last save failed: ${error}` : 'Last save failed — click to retry'}
      >
        <AlertTriangle size={14} />
        Retry save
      </Button>
    );
  }
  if (status === 'pending') {
    return (
      <Button size="sm" variant="primary" onClick={onClick} title="Force-save right now (bypass the 600ms debounce)">
        <Save size={14} />
        Save changes
      </Button>
    );
  }
  // idle — everything's persisted. Show a quiet, click-through control so
  // the user gets a "save now" affordance even when there's nothing to flush.
  return (
    <button
      type="button"
      onClick={onClick}
      title="All changes saved. Click to re-sync."
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-[13px] font-medium',
        'text-emerald-700 transition-colors hover:bg-emerald-50',
      )}
    >
      <Check size={14} />
      Saved
    </button>
  );
}

function UserAvatar() {
  return (
    <div
      className="grid h-8 w-8 place-items-center rounded-full bg-accent text-[11px] font-semibold text-white"
      title="John Doe"
    >
      JD
    </div>
  );
}
